import { logger } from "@/utils/logger";
import { getAnalysisData } from "../frequency/analysis.bridge";
import { pinia } from "../pinia";
import { useFrequencyStore } from "../frequency";
import { resolveMethodKey } from "../frequency/analysis.bridge";
import { assignVehiclesToServices, assignVehiclesFromAracIds, servicesHaveAracIds } from "./assignVehicles.algorithm";
import { saveVehicleDataToStorage } from "./helpers";
/**
 * Vuex vehicleAssignment mutations → Pinia sync actions (micro-6-1, 6S-13).
 */
export const fleetSyncActions = {
    /** Vuex: SET_VEHICLES */
    setVehicles(vehicles) {
        this.vehicles = vehicles;
    },
    /** Vuex: SET_ASSIGNMENTS */
    setAssignments(assignments) {
        this.assignments = assignments;
    },
    /** Vuex: SET_PARKUR_SURELERI */
    setParkurSureleri(data) {
        this.parkurSureleri = JSON.parse(JSON.stringify(data));
        const orig = this.originalParkurSureleri;
        if (!orig ||
            !orig.gidis ||
            !orig.donus ||
            Object.keys(orig.gidis).length === 0 ||
            Object.keys(orig.donus).length === 0) {
            logger.debug("Orijinal parkur süreleri ilk kez ayarlanıyor...");
            this.originalParkurSureleri = JSON.parse(JSON.stringify(data));
            try {
                localStorage.setItem("originalParkurSureleri", JSON.stringify(this.originalParkurSureleri));
            }
            catch (err) {
                logger.error("Orijinal parkur sürelerini localStorage'a kaydetme hatası:", err);
            }
        }
    },
    /** Vuex: SET_ORIGINAL_PARKUR_SURELERI */
    setOriginalParkurSureleri(payload) {
        this.originalParkurSureleri = JSON.parse(JSON.stringify(payload));
        try {
            localStorage.setItem("originalParkurSureleri", JSON.stringify(this.originalParkurSureleri));
            logger.debug("Orijinal parkur süreleri localStorage'a kaydedildi:", new Date());
        }
        catch (err) {
            logger.error("Orijinal parkur sürelerini localStorage'a kaydetme hatası:", err);
        }
    },
    /** Vuex: UPDATE_VEHICLE_TYPE (sync state patch) */
    patchVehicleType({ vehicleId, newType }) {
        this.vehicles = this.vehicles.map((vehicle) => {
            if (vehicle.id === vehicleId) {
                return {
                    ...vehicle,
                    arac_tipi: newType,
                };
            }
            return vehicle;
        });
    },
    /** Vuex: UPDATE_ASSIGNMENT_VEHICLE_TYPE */
    updateAssignmentVehicleType({ assignmentIndex, newType }) {
        if (assignmentIndex >= 0 && assignmentIndex < this.assignments.length) {
            this.assignments[assignmentIndex] = {
                ...this.assignments[assignmentIndex],
                arac_tipi: newType,
            };
        }
    },
    /** Vuex: UPDATE_PARKUR_SURELERI */
    updateParkurSureleri(parkurSureleri) {
        if (!this.originalParkurSureleri) {
            logger.debug("UPDATE işlemi öncesi orijinal parkur süreleri ayarlanıyor...");
            this.originalParkurSureleri = JSON.parse(JSON.stringify(this.parkurSureleri || parkurSureleri));
            try {
                localStorage.setItem("originalParkurSureleri", JSON.stringify(this.originalParkurSureleri));
            }
            catch (err) {
                logger.error("Orijinal parkur sürelerini localStorage'a kaydetme hatası:", err);
            }
        }
        this.parkurSureleri = JSON.parse(JSON.stringify(parkurSureleri));
        const analysisData = getAnalysisData();
        if (analysisData) {
            if (analysisData.gidis) {
                analysisData.gidis.parkur_suresi = parkurSureleri.gidis || {};
            }
            if (analysisData.donus) {
                analysisData.donus.parkur_suresi = parkurSureleri.donus || {};
            }
        }
        try {
            localStorage.setItem("currentParkurSureleri", JSON.stringify(this.parkurSureleri));
            logger.debug("Güncel parkur süreleri localStorage'a kaydedildi:", new Date());
        }
        catch (err) {
            logger.error("Güncel parkur sürelerini localStorage'a kaydetme hatası:", err);
        }
    },
    /** Vuex: RESET_ORIGINAL_PARKUR_SURELERI */
    resetOriginalParkurSureleri() {
        this.originalParkurSureleri = {};
        this.parkurSureleri = {};
        localStorage.removeItem("originalParkurSureleri");
        localStorage.removeItem("currentParkurSureleri");
        logger.debug("Parkur süreleri sıfırlandı");
    },
    /** Vuex: RESET_VEHICLE_TYPES */
    resetVehicleTypes() {
        this.vehicles = this.vehicles.map((vehicle) => ({
            ...vehicle,
            arac_tipi: "SOLO",
        }));
        this.assignments = this.assignments.map((assignment) => ({
            ...assignment,
            arac_tipi: "SOLO",
        }));
        logger.debug("Tüm araç tipleri sıfırlandı");
    },
    /** Vuex: CLEAR_VEHICLE_DATA */
    clearVehicleData() {
        this.vehicles = [];
        this.assignments = [];
        localStorage.removeItem("vehicleAssignments");
        logger.debug("Araç verileri temizlendi");
    },
};
/**
 * Vuex vehicleAssignment actions → Pinia (micro-6-3, 6S-13).
 * View bağlantısı yok; Vuex modülü paralel kalır.
 */
export const fleetAssignmentActions = {
    initializeVehicles({ services, parkurData }) {
        this.setParkurSureleri(parkurData);
        return this.assignVehicles(services);
    },
    assignVehicles(params) {
        let services = params;
        let preserveExisting = false;
        let mode = "auto"; // auto | fromAracId | greedy
        if (params && typeof params === "object" && !Array.isArray(params)) {
            if (params.services) {
                services = params.services;
                preserveExisting = params.preserveExisting || false;
            }
            if (params.mode)
                mode = params.mode;
        }
        void preserveExisting;
        if (!Array.isArray(services)) {
            logger.error("Geçersiz servis verisi:", services);
            return Promise.resolve({ vehicles: [], assignments: [] });
        }
        if (!services || services.length === 0) {
            this.setVehicles([]);
            this.setAssignments([]);
            return Promise.resolve({ vehicles: [], assignments: [] });
        }
        const useAracIds = mode === "fromAracId" ||
            (mode === "auto" && servicesHaveAracIds(services));
        logger.debug(`Araç ataması yapılıyor: ${services.length} servis (mode=${useAracIds ? "fromAracId" : "greedy"})`);
        try {
            const { vehicles, assignments } = useAracIds
                ? assignVehiclesFromAracIds(services)
                : assignVehiclesToServices(services);
            const formattedVehicles = vehicles.map((vehicle, index) => {
                const explicitId = Number(vehicle.assignments?.[0]?.arac_id ??
                    String(vehicle.id || "").replace(/^vehicle_pc_/, ""));
                return {
                    id: Number.isFinite(explicitId) && explicitId > 0 ? explicitId : index + 1,
                    arac_tipi: vehicle.type || "SOLO",
                    original_id: vehicle.id,
                    assignments: [],
                };
            });
            const vehicleIdByOriginal = new Map(formattedVehicles.map((v) => [v.original_id, v.id]));
            const formattedAssignments = assignments.map((assignment, index) => {
                const aracId = assignment.arac_id != null
                    ? Number(assignment.arac_id)
                    : vehicleIdByOriginal.get(assignment.vehicle_id) ??
                        vehicles.findIndex((v) => v.id === assignment.vehicle_id) + 1;
                const startMinutes = assignment.startTime;
                const endMinutes = assignment.endTime;
                const startHour = Math.floor(startMinutes / 60);
                const startMin = startMinutes % 60;
                const endHour = Math.floor(endMinutes / 60);
                const endMin = endMinutes % 60;
                const baslangic = `${String(startHour).padStart(2, "0")}:${String(startMin).padStart(2, "0")}`;
                const bitis = `${String(endHour).padStart(2, "0")}:${String(endMin).padStart(2, "0")}`;
                return {
                    id: `assignment_${index}`,
                    arac_id: aracId,
                    sefer_id: assignment.service_id,
                    baslangic: baslangic,
                    bitis: bitis,
                    yon: assignment.service.yon,
                    arac_tipi: assignment.service.arac_tipi || "SOLO",
                    original_vehicle_id: assignment.vehicle_id,
                };
            });
            this.setVehicles(formattedVehicles);
            this.setAssignments(formattedAssignments);
            logger.debug("Araç ataması tamamlandı:", {
                araç_sayısı: formattedVehicles.length,
                atama_sayısı: formattedAssignments.length,
                mode: useAracIds ? "fromAracId" : "greedy",
            });
            saveVehicleDataToStorage({
                vehicles: formattedVehicles,
                assignments: formattedAssignments,
            });
            return Promise.resolve({
                vehicles: formattedVehicles,
                assignments: formattedAssignments,
                originalVehicles: vehicles,
                originalAssignments: assignments,
            });
        }
        catch (error) {
            logger.error("Araç atama işlemi hatası:", error);
            return Promise.reject(error);
        }
    },
    async updateVehicleType({ vehicleId, newType }) {
        try {
            this.patchVehicleType({ vehicleId, newType });
            const vehicleAssignments = this.assignments.filter((a) => a.arac_id === vehicleId);
            for (const assignment of vehicleAssignments) {
                this.updateAssignmentVehicleType({
                    assignmentIndex: this.assignments.findIndex((a) => a.sefer_id === assignment.sefer_id),
                    newType: newType,
                });
                await this.updateServiceVehicleType({
                    serviceId: assignment.sefer_id,
                    newType: newType,
                });
            }
            saveVehicleDataToStorage(this);
            const methodKey = resolveMethodKey();
            window.dispatchEvent(new CustomEvent("service-plan-updated", {
                detail: { methodKey, forceRefresh: true },
            }));
            return true;
        }
        catch (error) {
            logger.error("Araç tipi güncelleme hatası:", error);
            throw error;
        }
    },
    updateServiceVehicleType({ serviceId, newType }) {
        try {
            logger.debug(`Servis #${serviceId} için araç tipi değiştiriliyor: ${newType}`);
            const methodKey = resolveMethodKey();
            const frequency = useFrequencyStore(pinia);
            const activePlans = frequency.activePlans;
            let foundService = null;
            let direction = null;
            if (activePlans && activePlans[methodKey]) {
                if (activePlans[methodKey].gidis) {
                    foundService = activePlans[methodKey].gidis.find((s) => s.id === serviceId);
                    if (foundService)
                        direction = "gidis";
                }
                if (!foundService && activePlans[methodKey].donus) {
                    foundService = activePlans[methodKey].donus.find((s) => s.id === serviceId);
                    if (foundService)
                        direction = "donus";
                }
            }
            if (foundService && direction) {
                logger.debug(`Servis bulundu: ${serviceId}, yön: ${direction}, güncellenecek tip: ${newType}`);
                frequency.updateService({
                    service: {
                        ...foundService,
                        yon: direction,
                        arac_tipi: newType,
                    },
                    methodKey,
                });
                try {
                    frequency.saveStateToPersistence();
                }
                catch {
                    /* persistence best-effort */
                }
            }
            else {
                logger.warn(`Servis ${serviceId} bulunamadı! ActivePlans içinde arama yapıldı.`);
            }
        }
        catch (error) {
            logger.error("Servis araç tipi güncelleme hatası:", error);
        }
    },
    loadSavedVehicleData() {
        try {
            logger.debug("LocalStorage'dan araç verileri yükleniyor...");
            const savedData = localStorage.getItem("vehicleAssignments");
            if (savedData) {
                const parsedData = JSON.parse(savedData);
                logger.debug("Yüklenen araç verisi:", parsedData);
                this.setVehicles(parsedData.vehicles || []);
                this.setAssignments(parsedData.assignments || []);
                if (parsedData.assignments && parsedData.assignments.length > 0) {
                    parsedData.assignments.forEach((assignment) => {
                        this.updateServiceVehicleType({
                            serviceId: assignment.sefer_id,
                            newType: assignment.arac_tipi,
                        });
                    });
                }
                logger.debug("Araç verileri başarıyla yüklendi!");
            }
            else {
                logger.debug("LocalStorage'da kaydedilmiş araç verisi bulunamadı");
            }
        }
        catch (error) {
            logger.error("Kaydedilmiş araç verilerini yükleme hatası:", error);
        }
    },
    ensureOriginalParkurSureleri() {
        const storedOriginals = localStorage.getItem("originalParkurSureleri");
        const initialOriginals = localStorage.getItem("initialParkurSureleri");
        if (initialOriginals) {
            logger.debug("İlk yüklenen API parkur süreleri kullanılıyor");
            this.setOriginalParkurSureleri(JSON.parse(initialOriginals));
        }
        else if (storedOriginals) {
            logger.debug("localStorage'dan orijinal parkur süreleri yükleniyor");
            this.setOriginalParkurSureleri(JSON.parse(storedOriginals));
        }
        logger.debug("Orijinal parkur süreleri kontrolü yapıldı");
    },
    calculateVehicleAssignments({ force = false, methodKey, } = {}) {
        logger.debug(`Araç atamaları hesaplanıyor (method: ${methodKey}, force: ${force})`);
        return useFrequencyStore(pinia).updateVehiclePlan(methodKey);
    },
};
